import { XLogsRoleStrings } from "../context/types/xlogsrole";
import { RoleAssignments, XLogsRole } from "../profile-sdk";
import { ClientAssignment } from "@xng/xng-backend";

/**
 * Extended role assignments that includes `isAssistant`.
 * The legacy `RoleAssignments` from profile-sdk doesn't have `isAssistant`;
 * the new SDK uses a `roles` bitmask where Assistant = 1.
 */
export interface XLogsRoleAssignments extends RoleAssignments {
  isAssistant?: boolean;
}

//* Enum: ExecutiveAdmin = 0, DelegatedAdmin = 1, Approver = 2, ProxyDataEntry = 3, ServiceProviderAutonomous = 4, ServiceProviderAssistant = 5

/**
 * Maps a role string/enum to its exact single flag.
 * Does not bundle implied lower-tier roles.
 */
export function ConstructRoleAssignmentsFromXLogsRoleEnumOrString(role: XLogsRoleStrings | XLogsRole): RoleAssignments {
  switch (role) {
    case "Service Provider - Autonomous":
    case XLogsRole.NUMBER_4:
      return { isAutonomous: true };
    case "Service Provider - Assistant":
    case XLogsRole.NUMBER_5:
      return { isAutonomous: false };
    case "Approver":
    case XLogsRole.NUMBER_2:
      return { isApprover: true };
    case "Proxy Data Entry":
    case XLogsRole.NUMBER_3:
      return { isProxyDataEntry: true };
    case "Delegated Admin":
    case XLogsRole.NUMBER_1:
      return { isDelegatedAdmin: true };
    case "Executive Admin":
    case XLogsRole.NUMBER_0:
      return { isExecutiveAdmin: true };
    default:
      return {};
  }
}

/**
 * Maps a role string/enum to its ServiceProviderRoles bitmask value.
 */
export function getRoleBitsFromXLogsRole(role: XLogsRoleStrings | XLogsRole): number {
  switch (role) {
    case "Service Provider - Assistant":
    case XLogsRole.NUMBER_5:
      return ROLE_BITS.Assistant;
    case "Service Provider - Autonomous":
    case XLogsRole.NUMBER_4:
      return ROLE_BITS.Autonomous;
    case "Proxy Data Entry":
    case XLogsRole.NUMBER_3:
      return ROLE_BITS.ProxyDataEntry;
    case "Approver":
    case XLogsRole.NUMBER_2:
      return ROLE_BITS.Approver;
    case "Delegated Admin":
    case XLogsRole.NUMBER_1:
      return ROLE_BITS.DelegatedAdmin;
    case "Executive Admin":
    case XLogsRole.NUMBER_0:
      return ROLE_BITS.ExecutiveAdmin;
    default:
      return 0;
  }
}

export function GetXlogsRoleFromRoleAssignments(roleAssignments: RoleAssignments): XLogsRole {
  if (roleAssignments.isExecutiveAdmin)
    return XLogsRole.NUMBER_0;
  if (roleAssignments.isDelegatedAdmin)
    return XLogsRole.NUMBER_1;
  if (roleAssignments.isApprover)
    return XLogsRole.NUMBER_2;
  if (roleAssignments.isProxyDataEntry)
    return XLogsRole.NUMBER_3;
  if (roleAssignments.isAutonomous)
    return XLogsRole.NUMBER_4;
  else
    return XLogsRole.NUMBER_5;
}

export function GetXlogsRoleEnumFromString(role: XLogsRoleStrings): XLogsRole {
  const roleRemap: Record<string, XLogsRole> = {
    "Executive Admin": XLogsRole.NUMBER_0,
    "Delegated Admin": XLogsRole.NUMBER_1,
    Approver: XLogsRole.NUMBER_2,
    "Proxy Data Entry": XLogsRole.NUMBER_3,
    "Service Provider - Autonomous": XLogsRole.NUMBER_4,
    "Service Provider - Assistant": XLogsRole.NUMBER_5,
  };

  return roleRemap[role];
}

export function GetXlogsRoleStringFromEnum(role: XLogsRole): XLogsRoleStrings {

  switch (role) {
    case XLogsRole.NUMBER_0:
      return "Executive Admin";
    case XLogsRole.NUMBER_1:
      return "Delegated Admin";
    case XLogsRole.NUMBER_2:
      return "Approver";
    case XLogsRole.NUMBER_3:
      return "Proxy Data Entry";
    case XLogsRole.NUMBER_4:
      return "Service Provider - Autonomous";
    case XLogsRole.NUMBER_5:
      return "Service Provider - Assistant";
    default:
      return "Service Provider - Assistant";
  }

}

export function GetXlogsRoleStringFromAssignment(role:  RoleAssignments): XLogsRoleStrings {

    if (role.isExecutiveAdmin)
      return "Executive Admin";
    if (role.isDelegatedAdmin)
      return "Delegated Admin";
    if (role.isApprover)
      return "Approver";
    if (role.isProxyDataEntry)
      return "Proxy Data Entry";
    if (role.isAutonomous)
      return "Service Provider - Autonomous";
    else
      return "Service Provider - Assistant";
}


export const xlogsRoleOptions: XLogsRoleStrings[] = [
  "Executive Admin",
  "Delegated Admin",
  "Approver",
  "Proxy Data Entry",
  "Service Provider - Autonomous",
  "Service Provider - Assistant",
]

// Individual role bit values based on bitwise flags
// 
// Mutually Exclusive Rules:
// - Assistant (1): Mutually exclusive with ALL other roles (can only be standalone)
// - Autonomous (2): Mutually exclusive with Assistant only
//
export const ROLE_BITS = {
  Assistant: 1,           // 00000001 - Mutually exclusive with ALL other roles
  Autonomous: 2,          // 00000010
  ProxyDataEntry: 4,      // 00000100
  Approver: 8,            // 00001000
  DelegatedAdmin: 16,     // 00010000
  ExecutiveAdmin: 32,     // 00100000
} as const;

// Valid role combinations as bit masks
// 
// Mutually Exclusive Rules:
// - Assistant (1): Cannot be combined with ANY other role
// - Delegated Admin (16) and Executive Admin (32): Cannot be combined together
//
export const VALID_ROLE_COMBINATIONS = [
  0,   // (none)
  1,   // Assistant (standalone only - mutually exclusive with all)
  2,   // Autonomous
  4,   // Proxy Data Entry
  6,   // Autonomous; Proxy Data Entry
  8,   // Approver
  10,  // Autonomous; Approver
  12,  // Proxy Data Entry; Approver
  14,  // Autonomous; Proxy Data Entry; Approver
  16,  // Delegated Admin
  18,  // Autonomous; Delegated Admin
  20,  // Proxy Data Entry; Delegated Admin
  22,  // Autonomous; Proxy Data Entry; Delegated Admin
  24,  // Approver; Delegated Admin
  26,  // Autonomous; Approver; Delegated Admin
  28,  // Proxy Data Entry; Approver; Delegated Admin
  30,  // Autonomous; Proxy Data Entry; Approver; Delegated Admin
  32,  // Executive Admin
  34,  // Autonomous; Executive Admin
  36,  // Proxy Data Entry; Executive Admin
  38,  // Autonomous; Proxy Data Entry; Executive Admin
  40,  // Approver; Executive Admin
  42,  // Autonomous; Approver; Executive Admin
  44,  // Proxy Data Entry; Approver; Executive Admin
  46,  // Autonomous; Proxy Data Entry; Approver; Executive Admin
] as const;

/**
 * Convert role assignments to a bitwise value.
 * Accepts both legacy RoleAssignments (profile-sdk) and XLogsRoleAssignments.
 */
export function roleAssignmentsToBits(roles: XLogsRoleAssignments): number {
  let bits = 0;
  
  if (roles.isProxyDataEntry) bits |= ROLE_BITS.ProxyDataEntry;
  if (roles.isApprover === true) bits |= ROLE_BITS.Approver;
  if (roles.isDelegatedAdmin === true) bits |= ROLE_BITS.DelegatedAdmin;
  if (roles.isExecutiveAdmin === true) bits |= ROLE_BITS.ExecutiveAdmin;
  
  // Assistant and Autonomous are mutually exclusive
  if (roles.isAutonomous === true) {
    bits |= ROLE_BITS.Autonomous;
  }
  if (roles.isAssistant === true) {
    bits |= ROLE_BITS.Assistant;
  }
  
  // If no roles are set, default to Assistant
  if (bits === 0) {
    bits |= ROLE_BITS.Assistant;
  }
  
  return bits;
}

/**
 * Convert ClientAssignment (from @xng/xng-backend) to a bitwise value.
 * The new SDK `roles` field is typed as individual enum members (0|1|2|4|8|16|32)
 * but the backend sends combined bitmask values (e.g. 6 = Autonomous + ProxyDataEntry).
 * We accept `number` to handle these combined values correctly.
 */
export function clientAssignmentToBits(assignment: ClientAssignment): number {
  const roles = assignment.roles as number | undefined;
  if (roles !== undefined && roles !== 0) {
    return roles;
  }
  
  // No roles set — default to Assistant
  return ROLE_BITS.Assistant;
}

/**
 * Convert a bitwise value to role assignments including isAssistant.
 */
export function bitsToRoleAssignments(bits: number): XLogsRoleAssignments {
  return {
    isAssistant: (bits & ROLE_BITS.Assistant) !== 0,
    isAutonomous: (bits & ROLE_BITS.Autonomous) !== 0,
    isProxyDataEntry: (bits & ROLE_BITS.ProxyDataEntry) !== 0,
    isApprover: (bits & ROLE_BITS.Approver) !== 0,
    isDelegatedAdmin: (bits & ROLE_BITS.DelegatedAdmin) !== 0,
    isExecutiveAdmin: (bits & ROLE_BITS.ExecutiveAdmin) !== 0,
  };
}

/**
 * Get role labels from a bitwise value
 */
export function getRoleLabelsFromBits(bits: number): string[] {
  const labels: string[] = [];
  
  if (bits & ROLE_BITS.Assistant) labels.push("Assistant");
  if (bits & ROLE_BITS.Autonomous) labels.push("Autonomous");
  if (bits & ROLE_BITS.ProxyDataEntry) labels.push("Proxy Data Entry");
  if (bits & ROLE_BITS.Approver) labels.push("Approver");
  if (bits & ROLE_BITS.DelegatedAdmin) labels.push("Delegated Admin");
  if (bits & ROLE_BITS.ExecutiveAdmin) labels.push("Executive Admin");
  
  return labels;
}

/**
 * Get bitwise value from role labels
 */
export function getBitsFromRoleLabels(labels: string[]): number {
  let bits = 0;
  
  for (const label of labels) {
    switch(label) {
      case "Assistant":
        bits |= ROLE_BITS.Assistant;
        break;
      case "Autonomous":
        bits |= ROLE_BITS.Autonomous;
        break;
      case "Proxy Data Entry":
        bits |= ROLE_BITS.ProxyDataEntry;
        break;
      case "Approver":
        bits |= ROLE_BITS.Approver;
        break;
      case "Delegated Admin":
        bits |= ROLE_BITS.DelegatedAdmin;
        break;
      case "Executive Admin":
        bits |= ROLE_BITS.ExecutiveAdmin;
        break;
    }
  }
  
  return bits;
}

/**
 * Check if a role combination is valid
 */
export function isValidRoleCombination(bits: number): boolean {
  return VALID_ROLE_COMBINATIONS.includes(bits as any);
}

/**
 * Get available role options based on the current user's role
 * @param isMsbEmployee - Whether the current user is an MSB employee (any MSB role)
 * @param currentUserRoles - The current user's role assignments
 * @returns Array of role labels sorted by bit value in descending order
 */
export function getAvailableRoleOptions(
  isMsbEmployee: boolean | undefined,
  currentUserRoles: RoleAssignments | undefined,
): string[] {
  const allRoles = ["Executive Admin", "Delegated Admin", "Approver", "Proxy Data Entry", "Autonomous", "Assistant"];
  
  let availableRoles: string[];
  
  // MSB Employee can assign all roles
  if (isMsbEmployee) {
    availableRoles = allRoles;
  }
  // Executive Admin can assign all roles (including Executive Admin)
  else if (currentUserRoles?.isExecutiveAdmin) {
    availableRoles = allRoles;
  }
  // Delegated Admin can assign all roles except Executive Admin (but can assign Delegated Admin)
  else if (currentUserRoles?.isDelegatedAdmin) {
    availableRoles = allRoles.filter(role => role !== "Executive Admin");
  }
  // Default: no roles available (shouldn't reach here if permissions are correct)
  else {
    availableRoles = [];
  }
  
  // Sort by bit value in descending order
  return availableRoles.sort((a, b) => {
    const bitsA = getBitsFromRoleLabels([a]);
    const bitsB = getBitsFromRoleLabels([b]);
    return bitsB - bitsA; // Descending order
  });
}

/**
 * Filter available role options based on current selection to ensure only valid combinations
 * @param currentSelection - Currently selected roles
 * @param availableRoles - Roles available based on user permissions
 * @returns Filtered array of role labels sorted by bit value in descending order
 */
export function getFilteredRoleOptions(
  currentSelection: string[],
  availableRoles: string[],
): string[] {
  if (currentSelection.length === 0) {
    return availableRoles;
  }
  
  const currentBits = getBitsFromRoleLabels(currentSelection);
  
  const filtered = availableRoles.filter(role => {
    const testBits = currentBits | getBitsFromRoleLabels([role]);
    return isValidRoleCombination(testBits);
  });
  
  // Sort by bit value in descending order
  return filtered.sort((a, b) => {
    const bitsA = getBitsFromRoleLabels([a]);
    const bitsB = getBitsFromRoleLabels([b]);
    return bitsB - bitsA; // Descending order
  });
}

/**
 * Check if a role option should be disabled based on current selection
 * @param role - The role to check
 * @param currentSelection - Currently selected roles
 * @returns true if the role would create an invalid combination
 */
export function isRoleOptionDisabled(
  role: string,
  currentSelection: string[],
): boolean {
  if (currentSelection.length === 0) {
    return false;
  }
  
  const currentBits = getBitsFromRoleLabels(currentSelection);
  const testBits = currentBits | getBitsFromRoleLabels([role]);
  
  return !isValidRoleCombination(testBits);
}
